/* eslint-disable tailwindcss/no-arbitrary-value */
import {
  Radio,
  RadioGroup,
  SelectionControlLabel,
  Typography,
} from '@one-thd/sui-atomic-components';
import { Checkmark, ErrorFilled } from '@one-thd/sui-icons';
import React, { ChangeEvent, JSX } from 'react';
import {
  IBtApiState,
  IBTResponse,
  IBuildertrendError,
} from '../../../../models/purchase-history';
import {
  integrationLinkErrorMessageHelper,
  integrationMessageHelper,
} from '../../../../utils/buildertrend-utils';
import { getWindow } from '../../../../utils/general';

import '../../../../styles/buildertrend.scss';

export interface IBuildertrendModalBody {
  userIsLinked: boolean;
  adminUnlinkRadio: 'customer' | 'user';
  setAdminUnlinkRadio: (value: 'customer' | 'user') => void;
  btApiState: IBtApiState;
  setBtApiState: (value: IBtApiState) => void;
}

interface IUnlinkingBodyElement {
  role: 'ADMIN' | 'PURCHASER';
  adminUnlinkRadio: 'customer' | 'user';
  setAdminUnlinkRadio: (value: 'customer' | 'user') => void;
  btApiState: IBtApiState;
}

interface ILinkingBodyElement {
  btApiState: IBtApiState;
}

/**
 * Buildertrend Body element and components for connecting Buildertrend Account
 */

const builderTrendSync = () => (
  <div
    className="sui-flex sui-items-center buildertrend__logo svg"
    style={{ width: '33%' }}
  >
    <img
      src="https://assets.thdstatic.com/images/v1/BuildertrendFull.svg"
      alt="Buildertrend Icon"
      width="210px"
      height="44px"
    />
  </div>
);

const renderSyncingLine = (): JSX.Element => (
  <div
    className="quickbooks-sync-progress__wavy-loader"
    style={{ width: '33%' }}
  >
    {[1, 2, 3, 4, 5].map((id) => (
      <span key={id} />
    ))}
  </div>
);

const homeDepotSync = () => (
  <div
    className="sui-flex sui-justify-end buildertrend-homedepot__logo svg"
    style={{ width: '28%', marginTop: '25px', marginBottom: '25px' }}
  >
    <img
      src="https://assets.thdstatic.com/images/v1/HomeDepotLogo.svg"
      alt="Home Depot Logo"
      width="150px"
      height="150px"
    />
  </div>
);

const builderTrendAuthorizeShare = () => {
  return (
    <div className="sui-flex sui-justify-between sui-border-b-2 sui-border-solid sui-border-b-strong">
      {homeDepotSync()}
      {renderSyncingLine()}
      {builderTrendSync()}
    </div>
  );
};

const firstInfoList = () => (
  <ul
    className="sui-list-disc sui-pb-2"
    style={{ padding: '5px 5px 5px 20px' }}
  >
    <li>
      Sync your Pro Xtra purchase history and products on your Pro Xtra My List
      page with Buildertrend
    </li>
    <li>
      Easily build proposals for your customers using products from The Home
      Depot when using Buildertrend’s software
    </li>
    <li>
      Add products to your cart on homedepot.com directly from your Buildertrend
      account
    </li>
    <li>
      Get a 1% discount on products purchased in-store and online from The Home
      Depot*
    </li>
  </ul>
);

const builderTrendTermsAndConditions = () => {
  return (
    <button
      type="button"
      className="sui-pb-1 sui-underline"
      onClick={() => {
        getWindow()?.open('https://buildertrend.com/terms-and-conditions/');
      }}
    >
      Terms and Conditions Agreement
    </button>
  );
};

const builderTrendPrivacyStatementLink = () => {
  return (
    <button
      type="button"
      className="sui-pb-1 sui-underline"
      onClick={() => {
        getWindow()?.open('https://buildertrend.com/company/privacy-policy/');
      }}
    >
      Privacy Policy
    </button>
  );
};

const secondInfoList = () => (
  <ul
    className="sui-list-disc sui-pb-2"
    style={{ padding: '5px 5px 5px 20px' }}
  >
    <li>Your Pro Xtra customer number and company name;</li>
    <li>
      The SKU number, image and price for products stored on the My List page;
      and
    </li>
    <li>
      Your Pro Xtra purchase history (for the prior 25 months), including SKU
      numbers, quantities, purchase price, date, order number, job name, and
      store number.
    </li>
  </ul>
);

const buildertrendInfo = () => {
  return (
    <div className="sui-pt-8 sui-border-t-2 sui-border-w-">
      <div className="sui-pb-1">
        <Typography variant="body-base">
          We are excited to offer Pro Xtra members who use Buildertrend&apos;s
          software an option to link your Pro Xtra and Buildertrend accounts to
          enable you to:
        </Typography>
      </div>
      {firstInfoList()}
      <div className="sui-pb-1">
        <Typography variant="body-base">
          By clicking “Connect” you: (i) represent that you have a valid account
          with Buildertrend; (ii) acknowledge that your use of the Buildertrend
          platform is governed by Buildertrend&apos;s{' '}
          {builderTrendTermsAndConditions()} and{' '}
          {builderTrendPrivacyStatementLink()}; and (iii) agree that The Home
          Depot will share the following information with Buildertrend (and that
          this information will be governed by Buildertrend&apos;s{' '}
          {builderTrendPrivacyStatementLink()}):
        </Typography>
      </div>
      {secondInfoList()}
      <Typography variant="body-xs">
        <strong>*</strong> Excludes special orders, may not be combined with
        other offers or pricing terms, discount applied before taxes and
        delivery charges.
      </Typography>
    </div>
  );
};

/**
 * Buildertrend Elements for Successful and Failed linking
 */

const successfulLinkingBodyElement = () => {
  return (
    <div className="sui-flex">
      <div className="sui-w-11">
        <Checkmark color="success" />
      </div>
      <div>
        <div className="sui-text-[#2596be] sui-font-bold">
          <Typography variant="body-base" weight="bold">
            Your Buildertrend account is now linked.
          </Typography>
        </div>
        <div className="sui-pt-2">
          <Typography>
            Please allow 24-48 hours depending on the size of your account
            history to display in your Buildertrend account.
          </Typography>
        </div>
      </div>
    </div>
  );
};

const failedLinkingBodyElement = (errCode: string) => {
  const btLinkErrorMessage = integrationMessageHelper[errCode]
    ? integrationLinkErrorMessageHelper[errCode]
    : integrationLinkErrorMessageHelper.DEFAULT;

  return (
    <div className="sui-flex">
      <div className="sui-w-16">
        <ErrorFilled color="danger" />
      </div>
      <div>
        <div className="sui-text-danger sui-font-bold ">
          <Typography variant="body-base" weight="bold">
            Unable to Connect Accounts
          </Typography>
        </div>
        <div className="sui-pt-2">
          <Typography>{btLinkErrorMessage}</Typography>
        </div>
      </div>
    </div>
  );
};

const LinkingBodyElement = (props: ILinkingBodyElement) => {
  const { btApiState } = props;
  if (btApiState.isLoading) {
    return <>Loading here...</>;
  }

  if ((btApiState?.btResponse as IBTResponse | IBuildertrendError)?.errors) {
    return failedLinkingBodyElement(
      (btApiState?.btResponse as IBTResponse | IBuildertrendError)?.errors?.[0]
        ?.errorCode,
    );
  }
  if ((btApiState?.btResponse as IBTResponse)?.linked) {
    return successfulLinkingBodyElement();
  }
  return (
    <>
      {builderTrendAuthorizeShare()}
      {buildertrendInfo()}
    </>
  );
};

/**
 * Buildertrend Body element and components for disconnecting Buildertrend Account
 */

const adminUnlinkRadioElement = (props: {
  adminUnlinkRadio: 'customer' | 'user';
  setAdminUnlinkRadio: (value: 'customer' | 'user') => void;
}) => (
  <div className="sui-pt-2">
    <RadioGroup
      orientation="vertical"
      aria-labelledby="demo-controlled-radio-buttons-group"
      name="controlled-radio-buttons-group"
      value={props.adminUnlinkRadio}
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        props.setAdminUnlinkRadio(event.target.value as 'customer' | 'user');
      }}
    >
      <SelectionControlLabel value="customer" label="Company Account">
        <Radio />
      </SelectionControlLabel>
      <p style={{ padding: '0px 0px 20px 32px' }}>
        This will disconnect The Home Depot from Buildertrend and you will no
        longer be able to sync information from past purchases and saved
        products.
      </p>
      <SelectionControlLabel value="user" label="User Account Only">
        <Radio />
      </SelectionControlLabel>
      <p style={{ paddingLeft: '32px' }}>
        You will no longer be able to send items to Buildertrend. Other users on
        this account will still be able to sync with Buildertrend however.
      </p>
    </RadioGroup>
  </div>
);

const adminUnlink = (props: {
  adminUnlinkRadio: 'customer' | 'user';
  setAdminUnlinkRadio: (value: 'customer' | 'user') => void;
}) => (
  <div>
    {/* <Typography variant="body-base" weight="bold">
      Disconnect Buildertrend Account 
    </Typography> */}
    <Typography variant="body-base" weight="bold">
      Which account would you like to disconnect?
    </Typography>
    {adminUnlinkRadioElement(props)}
  </div>
);

const userUnlink = () => (
  <div>
    {/*  <Typography variant="body-base" weight="bold">
      Disconnect Buildertrend Account
    </Typography> */}

    <p style={{ padding: '25px 0px 30px 0px' }}>
      You will no longer be able to send items to Buildertrend. Other users on
      this account will still be able to sync with Buildertrend however.
    </p>
  </div>
);

/**
 * Successful UI for Disconnect
 */

const successfulUnlinkBodyElement = () => {
  return (
    <div className="sui-flex">
      <div className="sui-w-11">
        <Checkmark color="success" />
      </div>
      <div className="sui-text-[#00873C] sui-font-bold">
        <Typography variant="body-base" weight="bold">
          Your Buildertrend account has been disconnected.
        </Typography>
      </div>
    </div>
  );
};

/**
 * Failed UI for Disconnect
 */

const failedUnlinkingBodyElement = () => {
  return (
    <div className="sui-flex">
      <div className="sui-w-16">
        <ErrorFilled color="danger" />
      </div>
      <div>
        <div className="sui-text-danger sui-font-bold sui-px-1">
          <Typography variant="body-base" weight="bold">
            Unable to Disconnect Accounts
          </Typography>
        </div>
        <div className="sui-pt-2">
          <Typography>
            {' '}
            We were unable to disconnect your account from Buildertrend. Please
            close this window and retry by clicking the Disconnect Buildertrend
            link again, or contact Customer Support if you continue to receive
            this error.
          </Typography>
        </div>
      </div>
    </div>
  );
};

/**
 *
 * @param props
 * @returns
 *
 * Handles the rendering of the Unlink default UI, along with the linking and
 * unlinking api UI changes.
 */
const UnlinkingBodyElement = (props: IUnlinkingBodyElement) => {
  const { role, adminUnlinkRadio, setAdminUnlinkRadio, btApiState } = props;

  if (btApiState.isLoading) {
    return <>Loading here...</>;
  }

  if ((btApiState?.btResponse as IBTResponse | IBuildertrendError)?.errors) {
    return failedUnlinkingBodyElement();
  }
  if (btApiState?.btResponse) {
    return successfulUnlinkBodyElement();
  }

  return (
    <div>
      <div className="builder-trend-response">
        {role === 'ADMIN'
          ? adminUnlink({ adminUnlinkRadio, setAdminUnlinkRadio })
          : userUnlink()}
      </div>
    </div>
  );
};

/**
 * Function that handles the Buildertrend modal body for Connecting and Disconnecting
 */

export const BuildertrendModalBody = (props: IBuildertrendModalBody) => {
  const { userIsLinked, adminUnlinkRadio, setAdminUnlinkRadio, btApiState } =
    props;

  const UnlinkingElementProps: IUnlinkingBodyElement = {
    role: 'ADMIN',
    adminUnlinkRadio,
    setAdminUnlinkRadio,
    btApiState,
  };

  const LinkingElementProps: ILinkingBodyElement = {
    btApiState,
  };

  if (userIsLinked) {
    return (
      <>
        <UnlinkingBodyElement {...UnlinkingElementProps} />
      </>
    );
  }
  return <LinkingBodyElement {...LinkingElementProps} />;
};
